import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const DOC_TYPES = ['OC', 'CC', 'Fire_NOC', 'Trade_License'];
const DOC_LABELS = {
  OC: 'Occupancy Certificate',
  CC: 'Completion Certificate',
  Fire_NOC: 'Fire NOC',
  Trade_License: 'Trade License',
};

const STATUS_COLORS = {
  Valid: 'bg-green-100 text-green-700 border-green-200',
  Expired: 'bg-red-100 text-red-700 border-red-200',
  Pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const STATUS_ICONS = {
  Valid: 'CheckCircle',
  Expired: 'XCircle',
  Pending: 'Clock',
};

const ComplianceFormModal = ({ record, centers, onClose, onSave }) => {
  const [form, setForm] = useState({
    center_id: record?.center_id || '',
    document_type: record?.document_type || 'OC',
    status: record?.status || 'Pending',
    expiry_date: record?.expiry_date || '',
    notes: record?.notes || '',
    file_url: record?.file_url || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await onSave(record?.id, form);
      onClose();
    } catch (err) {
      alert(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {record?.id ? 'Edit Compliance Record' : 'Add Compliance Record'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Center *</label>
            <select
              value={form?.center_id}
              onChange={e => setForm(f => ({ ...f, center_id: e?.target?.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select center</option>
              {centers?.map(c => <option key={c?.id} value={c?.id}>{c?.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
            <select
              value={form?.document_type}
              onChange={e => setForm(f => ({ ...f, document_type: e?.target?.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {DOC_TYPES?.map(t => <option key={t} value={t}>{DOC_LABELS?.[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
            <select
              value={form?.status}
              onChange={e => setForm(f => ({ ...f, status: e?.target?.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="Valid">Valid</option>
              <option value="Expired">Expired</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
            <input
              type="date"
              value={form?.expiry_date}
              onChange={e => setForm(f => ({ ...f, expiry_date: e?.target?.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File URL</label>
            <input
              type="url"
              value={form?.file_url}
              onChange={e => setForm(f => ({ ...f, file_url: e?.target?.value }))}
              placeholder="https://..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form?.notes}
              onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Saving...' : 'Save Record'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CenterCompliancePage = () => {
  const { userProfile } = useAuth();
  const [records, setRecords] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [centerFilter, setCenterFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const isSuperAdmin = userProfile?.role === 'super_admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [complianceRes, centersRes] = await Promise.all([
        supabase?.from('center_compliance')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('centers')?.select('id, name, city')?.eq('is_active', true),
      ]);
      if (complianceRes?.error) throw complianceRes?.error;
      setRecords(complianceRes?.data || []);
      setCenters(centersRes?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (id, form) => {
    if (id) {
      const { error } = await supabase?.from('center_compliance')?.update(form)?.eq('id', id);
      if (error) throw error;
    } else {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { error } = await supabase?.from('center_compliance')?.insert({ ...form, created_by: user?.id });
      if (error) throw error;
    }
    await loadData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this compliance record?')) return;
    const { error } = await supabase?.from('center_compliance')?.delete()?.eq('id', id);
    if (error) { alert(error?.message); return; }
    await loadData();
  };

  const filtered = records?.filter(r => {
    const matchCenter = centerFilter === 'all' || r?.center_id === centerFilter;
    const matchStatus = statusFilter === 'all' || r?.status === statusFilter;
    return matchCenter && matchStatus;
  });

  const stats = {
    total: records?.length,
    valid: records?.filter(r => r?.status === 'Valid')?.length,
    expired: records?.filter(r => r?.status === 'Expired')?.length,
    pending: records?.filter(r => r?.status === 'Pending')?.length,
  };

  const getDaysToExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const diff = new Date(expiryDate) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getCenterName = (centerId) => centers?.find(c => c?.id === centerId)?.name || centerId;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading compliance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Center Compliance</h1>
          <p className="text-gray-600 mt-1">Track OC, CC, Fire NOC, and Trade License status</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => { setEditRecord(null); setShowForm(true); }} className="flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add Record
          </Button>
        )}
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
      )}
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Documents', value: stats?.total, icon: 'FileText', color: 'bg-blue-500' },
          { label: 'Valid', value: stats?.valid, icon: 'CheckCircle', color: 'bg-green-500' },
          { label: 'Expired', value: stats?.expired, icon: 'XCircle', color: 'bg-red-500' },
          { label: 'Pending', value: stats?.pending, icon: 'Clock', color: 'bg-yellow-500' },
        ]?.map(s => (
          <div key={s?.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${s?.color} flex items-center justify-center`}>
              <Icon name={s?.icon} size={18} color="white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{s?.value}</p>
              <p className="text-xs text-gray-500">{s?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Center</label>
          <select
            value={centerFilter}
            onChange={e => setCenterFilter(e?.target?.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Centers</option>
            {centers?.map(c => <option key={c?.id} value={c?.id}>{c?.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e?.target?.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Status</option>
            <option value="Valid">Valid</option>
            <option value="Expired">Expired</option>
            <option value="Pending">Pending</option>
          </select>
        </div>
      </div>
      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Center</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Document</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Expiry Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Days Left</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Notes</th>
                {isSuperAdmin && <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered?.length === 0 ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 7 : 6} className="text-center py-12 text-gray-500">
                    <Icon name="FileText" className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    No compliance records found
                  </td>
                </tr>
              ) : filtered?.map(record => {
                const daysLeft = getDaysToExpiry(record?.expiry_date);
                return (
                  <tr key={record?.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{getCenterName(record?.center_id)}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-700">{DOC_LABELS?.[record?.document_type] || record?.document_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS?.[record?.status]}`}>
                        <Icon name={STATUS_ICONS?.[record?.status]} size={12} />
                        {record?.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {record?.expiry_date ? new Date(record.expiry_date)?.toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {daysLeft !== null ? (
                        <span className={`text-sm font-semibold ${
                          daysLeft < 0 ? 'text-red-600' :
                          daysLeft < 30 ? 'text-orange-600' :
                          daysLeft < 90 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d`}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{record?.notes || '—'}</td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {record?.file_url && (
                            <a href={record?.file_url} target="_blank" rel="noreferrer"
                              className="text-blue-600 hover:text-blue-700">
                              <Icon name="ExternalLink" size={16} />
                            </a>
                          )}
                          <button onClick={() => { setEditRecord(record); setShowForm(true); }}
                            className="text-gray-400 hover:text-gray-600">
                            <Icon name="Pencil" size={16} />
                          </button>
                          <button onClick={() => handleDelete(record?.id)}
                            className="text-red-400 hover:text-red-600">
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {showForm && (
        <ComplianceFormModal
          record={editRecord}
          centers={centers}
          onClose={() => { setShowForm(false); setEditRecord(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default CenterCompliancePage;
